import { CalendarX, Pencil, Plus, Receipt, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import { MaskedInput } from '@/components/form/masked-input';
import { DataTable } from '@/components/data-table/data-table';
import { columnsFor } from '@/components/data-table/features';
import { EmptyState } from '@/components/empty-state';
import { Field } from '@/components/form/field';
import { FormError } from '@/components/form/form-error';
import { SelectField } from '@/components/form/select-field';
import { useZodForm } from '@/components/form/use-zod-form';
import { ConfirmDialog } from '@/components/modal/confirm-dialog';
import { Modal } from '@/components/modal/modal';
import { ModalActions } from '@/components/modal/modal-actions';
import { MoneyValue } from '@/components/money-value';
import { PageHeader } from '@/components/page-header';
import { QueryState } from '@/components/query-state';
import { Page } from '@/components/shell/page';
import { StatusBadge } from '@/components/status-badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/features/auth/use-auth';
import { moneyInput } from '@/lib/schemas';
import { toast, toastApiError } from '@/lib/toast';
import {
  CATEGORY_LABELS,
  type FixedExpense,
  type FixedExpenseCategory,
  monthLabel,
  recentMonths,
  useChangeFixedExpense,
  useCreateFixedExpense,
  useDeleteFixedExpense,
  useEndFixedExpense,
  useFixedExpenses,
} from '../api/finance';

const month = z.string().regex(/^\d{4}-\d{2}$/, 'Escolha o mês.');
const thisMonth = () => recentMonths(1)[0]!;

/**
 * Despesas fixas da empresa (ADR-050, decisão do dono: cadastro mensal manual). O valor tem
 * vigência: mudar o valor vale a partir de um mês, e o mês fechado continua com o valor que teve.
 * Só se exclui a despesa que ainda não começou; a que já valeu se encerra.
 */
export function FixedExpensesPage() {
  const expenses = useFixedExpenses();
  const { can } = useAuth();
  const canEdit = can('organization.manage');
  const [creating, setCreating] = useState(false);
  const [changing, setChanging] = useState<FixedExpense | null>(null);
  const [ending, setEnding] = useState<FixedExpense | null>(null);
  const [deleting, setDeleting] = useState<FixedExpense | null>(null);
  const remove = useDeleteFixedExpense();

  return (
    <Page size="lg">
      <PageHeader
        title="Despesas fixas"
        description="Custos do mês que não dependem das vendas: entram no resultado operacional e no ponto de equilíbrio."
        {...(canEdit
          ? {
              actions: (
                <Button onClick={() => setCreating(true)}>
                  <Plus aria-hidden /> Nova despesa
                </Button>
              ),
            }
          : {})}
      />
      <Card>
        <CardContent className="pt-4 sm:pt-6">
          <QueryState
            query={expenses}
            isEmpty={(data) => data.length === 0}
            empty={
              <EmptyState
                icon={Receipt}
                title="Nenhuma despesa fixa"
                description="Cadastre aluguel, salários, contador, sistemas: sem elas, o resultado operacional e o ponto de equilíbrio ficam de fora."
              />
            }
          >
            {(data) => (
              <ExpensesTable
                expenses={data}
                canEdit={canEdit}
                onChange={setChanging}
                onEnd={setEnding}
                onDelete={setDeleting}
              />
            )}
          </QueryState>
        </CardContent>
      </Card>

      <Modal open={creating} onOpenChange={setCreating} title="Nova despesa fixa">
        {creating && <CreateForm onClose={() => setCreating(false)} />}
      </Modal>
      <Modal
        open={changing !== null}
        onOpenChange={(open) => !open && setChanging(null)}
        title="Mudar o valor"
        description="O valor novo vale a partir do mês escolhido; os meses antes dele ficam com o valor antigo."
      >
        {changing && <ChangeForm expense={changing} onClose={() => setChanging(null)} />}
      </Modal>
      <Modal
        open={ending !== null}
        onOpenChange={(open) => !open && setEnding(null)}
        title="Encerrar despesa"
        description="Deixa de entrar no resultado depois do mês escolhido. O histórico fica."
      >
        {ending && <EndForm expense={ending} onClose={() => setEnding(null)} />}
      </Modal>
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Excluir despesa"
        description={`"${deleting?.name ?? ''}" ainda não começou a valer e será apagada. Não dá para desfazer.`}
        isLoading={remove.isPending}
        onConfirm={() =>
          deleting &&
          void remove
            .mutateAsync(deleting.id)
            .then(() => {
              toast.success('Despesa excluída.');
              setDeleting(null);
            })
            .catch((error: unknown) => toastApiError(error))
        }
      />
    </Page>
  );
}

const expenseColumn = columnsFor<FixedExpense>();

function ExpensesTable({
  expenses,
  canEdit,
  onChange,
  onEnd,
  onDelete,
}: {
  expenses: FixedExpense[];
  canEdit: boolean;
  onChange: (expense: FixedExpense) => void;
  onEnd: (expense: FixedExpense) => void;
  onDelete: (expense: FixedExpense) => void;
}) {
  const current = thisMonth();
  const columns = [
    expenseColumn.accessor('name', {
      header: 'Despesa',
      cell: ({ row }) => (
        <div>
          {row.original.name}
          <span className="block text-xs text-muted-foreground">
            {CATEGORY_LABELS[row.original.category]}
          </span>
        </div>
      ),
    }),
    expenseColumn.accessor('amount', {
      header: 'Valor por mês',
      meta: { align: 'right' },
      cell: ({ row }) => <MoneyValue value={row.original.amount} />,
    }),
    expenseColumn.accessor('startMonth', {
      header: 'Vigência',
      cell: ({ row }) => {
        const { startMonth, endMonth } = row.original;
        const status =
          endMonth && endMonth < current ? 'ended' : startMonth > current ? 'future' : 'active';
        return (
          <div className="space-y-1">
            <p className="text-sm">
              {monthLabel(startMonth)} {endMonth ? `a ${monthLabel(endMonth)}` : 'em diante'}
              {row.original.dueDay ? ` · vence dia ${row.original.dueDay}` : ''}
            </p>
            <StatusBadge
              tone={status === 'active' ? 'success' : status === 'future' ? 'info' : 'neutral'}
            >
              {status === 'active'
                ? 'Valendo'
                : status === 'future'
                  ? 'Começa depois'
                  : 'Encerrada'}
            </StatusBadge>
          </div>
        );
      },
    }),
    ...(canEdit
      ? [
          expenseColumn.display({
            id: 'actions',
            header: '',
            meta: { align: 'right' },
            cell: ({ row }) => {
              const expense = row.original;
              const ended = expense.endMonth !== null && expense.endMonth < current;
              if (ended) return null;
              return (
                <div className="flex justify-end gap-1">
                  <Button variant="ghost" size="sm" onClick={() => onChange(expense)}>
                    <Pencil aria-hidden /> Mudar valor
                  </Button>
                  {expense.startMonth > current ? (
                    <Button variant="ghost" size="sm" onClick={() => onDelete(expense)}>
                      <Trash2 aria-hidden /> Excluir
                    </Button>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => onEnd(expense)}>
                      <CalendarX aria-hidden /> Encerrar
                    </Button>
                  )}
                </div>
              );
            },
          }),
        ]
      : []),
  ];
  return <DataTable data={expenses} columns={columns} getRowId={(expense) => expense.id} />;
}

const createSchema = z.object({
  name: z.string().trim().min(2, 'Dê um nome à despesa.').max(120),
  category: z.string().min(1, 'Escolha o grupo.'),
  amount: moneyInput({ required: true }),
  startMonth: month,
  dueDay: z
    .string()
    .trim()
    .refine(
      (value) => value === '' || (Number(value) >= 1 && Number(value) <= 31),
      'Dia de 1 a 31.',
    ),
});

function CreateForm({ onClose }: { onClose: () => void }) {
  const create = useCreateFixedExpense();
  const form = useZodForm(createSchema, {
    name: '',
    category: 'OTHER',
    amount: '',
    startMonth: thisMonth(),
    dueDay: '',
  });
  const submit = form.handleSubmit(async ({ amount, category, dueDay, ...rest }) => {
    if (!amount) return;
    const saved = await create
      .mutateAsync({
        ...rest,
        amount,
        category: category as FixedExpenseCategory,
        endMonth: null,
        dueDay: dueDay === '' ? null : Number(dueDay),
      })
      .catch(() => null);
    if (!saved) return;
    toast.success('Despesa cadastrada. Já entra no resultado dos meses em que vale.');
    onClose();
  });
  return (
    <form onSubmit={submit} className="grid gap-3" noValidate>
      <FormError error={create.error} fallback="Não foi possível cadastrar." />
      <Field control={form.control} name="name" label="Nome" required>
        {(field) => <Input {...field} placeholder="Ex.: Aluguel do galpão" />}
      </Field>
      <SelectField
        control={form.control}
        name="category"
        label="Grupo"
        required
        options={(Object.keys(CATEGORY_LABELS) as FixedExpenseCategory[]).map((value) => ({
          value,
          label: CATEGORY_LABELS[value],
        }))}
      />
      <Field control={form.control} name="amount" label="Valor por mês (R$)" required>
        {(field) => <MaskedInput {...field} mask="money" />}
      </Field>
      <Field control={form.control} name="startMonth" label="A partir de" required>
        {(field) => <Input {...field} type="month" />}
      </Field>
      <Field
        control={form.control}
        name="dueDay"
        label="Dia do vencimento"
        hint="Para a previsão de caixa. Sem o dia, ela conta no dia 1, o mais cauteloso."
      >
        {(field) => <MaskedInput {...field} mask="integer" placeholder="Ex.: 10" />}
      </Field>
      <div className="flex justify-end gap-2">
        <ModalActions
          onCancel={onClose}
          confirmType="submit"
          confirmLabel="Cadastrar"
          isLoading={create.isPending}
        />
      </div>
    </form>
  );
}

const changeSchema = z.object({ amount: moneyInput({ required: true }), fromMonth: month });

function ChangeForm({ expense, onClose }: { expense: FixedExpense; onClose: () => void }) {
  const change = useChangeFixedExpense();
  const current = thisMonth();
  const form = useZodForm(changeSchema, {
    amount: '',
    fromMonth: expense.startMonth > current ? expense.startMonth : current,
  });
  const submit = form.handleSubmit(async ({ amount, fromMonth }) => {
    if (!amount) return;
    const saved = await change.mutateAsync({ id: expense.id, amount, fromMonth }).catch(() => null);
    if (!saved) return;
    toast.success(`Novo valor a partir de ${monthLabel(fromMonth)}.`);
    onClose();
  });
  return (
    <form onSubmit={submit} className="grid gap-3" noValidate>
      <FormError error={change.error} fallback="Não foi possível mudar o valor." />
      <p className="text-sm text-muted-foreground">
        {expense.name}: hoje <MoneyValue value={expense.amount} /> por mês.
      </p>
      <Field control={form.control} name="amount" label="Novo valor por mês (R$)" required>
        {(field) => <MaskedInput {...field} mask="money" />}
      </Field>
      <Field control={form.control} name="fromMonth" label="A partir de" required>
        {(field) => <Input {...field} type="month" />}
      </Field>
      <div className="flex justify-end gap-2">
        <ModalActions
          onCancel={onClose}
          confirmType="submit"
          confirmLabel="Salvar"
          isLoading={change.isPending}
        />
      </div>
    </form>
  );
}

const endSchema = z.object({ lastMonth: month });

function EndForm({ expense, onClose }: { expense: FixedExpense; onClose: () => void }) {
  const end = useEndFixedExpense();
  const form = useZodForm(endSchema, { lastMonth: thisMonth() });
  const submit = form.handleSubmit(async ({ lastMonth }) => {
    const saved = await end.mutateAsync({ id: expense.id, lastMonth }).catch(() => null);
    if (!saved) return;
    toast.success(`${expense.name} encerrada em ${monthLabel(lastMonth)}.`);
    onClose();
  });
  return (
    <form onSubmit={submit} className="grid gap-3" noValidate>
      <FormError error={end.error} fallback="Não foi possível encerrar." />
      <Field control={form.control} name="lastMonth" label="Último mês em que vale" required>
        {(field) => <Input {...field} type="month" />}
      </Field>
      <div className="flex justify-end gap-2">
        <ModalActions
          onCancel={onClose}
          confirmType="submit"
          confirmLabel="Encerrar"
          isLoading={end.isPending}
        />
      </div>
    </form>
  );
}
